/**
 * X (Twitter) Authentication Service
 * Handles OAuth 2.0 PKCE flow for connecting X accounts to Sendifier.
 * Mirrors the pattern of facebook-auth.service.ts.
 *
 * Flow:
 *  1. generatePkce()        — create code_verifier + code_challenge
 *  2. buildAuthUrl()        — construct the Twitter OAuth 2.0 URL
 *  3. WebBrowser.openAuthSessionAsync() — open in-app browser (caller's responsibility)
 *  4. exchangeCode()        — POST /api/x-twitter/auth/exchange → returns tokens + user profile
 */

import * as crypto from 'expo-crypto';
import Constants from 'expo-constants';

export interface XTwitterUser {
  id: string;
  username: string;
  name: string;
  profile_image_url?: string;
}

export interface XTwitterAuthTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at: string;
  user: XTwitterUser;
}

export interface XTwitterPkce {
  codeVerifier: string;
  codeChallenge: string;
  state: string;
}

const X_SCOPES = 'tweet.read tweet.write users.read dm.read dm.write offline.access';
const X_AUTH_BASE = 'https://twitter.com/i/oauth2/authorize';

class XTwitterAuthService {
  private clientId: string;
  private redirectUri: string;

  constructor() {
    const appConfig =
      Constants.expoConfig?.extra ||
      (Constants as any)?.manifest2?.extra ||
      (Constants as any)?.manifest?.extra;

    this.clientId =
      appConfig?.xTwitterClientId ||
      '';

    this.redirectUri =
      appConfig?.xTwitterRedirectUri ||
      '';

    console.log('🔍 [XTwitterAuthService] Configuration loaded:', {
      hasClientId: !!this.clientId,
      redirectUri: this.redirectUri || '(missing)',
      isConfigured: this.isConfigured(),
      appConfigHasClientId: !!appConfig?.xTwitterClientId,
      appConfigHasRedirectUri: !!appConfig?.xTwitterRedirectUri,
    });
  }

  isConfigured(): boolean {
    return !!(this.clientId && this.redirectUri);
  }

  getRedirectUri(): string {
    return this.redirectUri;
  }

  getClientId(): string {
    return this.clientId;
  }

  /**
   * Generate PKCE code_verifier, code_challenge, and state.
   * Quoted from X-Integrator/src/auth.js base64url + SHA256 PKCE logic,
   * adapted to use expo-crypto for React Native compatibility.
   */
  async generatePkce(): Promise<XTwitterPkce> {
    // Generate 32 random bytes → base64url (no padding, url-safe)
    const verifierBytes = await crypto.getRandomBytesAsync(32);
    const codeVerifier = this.base64url(verifierBytes);

    // SHA256 of verifier → base64url
    const challengeBytes = await crypto.digestStringAsync(
      crypto.CryptoDigestAlgorithm.SHA256,
      codeVerifier,
      { encoding: crypto.CryptoEncoding.BASE64 }
    );
    // digestStringAsync returns standard base64 — convert to url-safe
    const codeChallenge = challengeBytes
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');

    const stateBytes = await crypto.getRandomBytesAsync(16);
    const state = this.base64url(stateBytes);

    return { codeVerifier, codeChallenge, state };
  }

  /**
   * Build the full X OAuth 2.0 authorization URL.
   * Quoted from X-Integrator/src/auth.js GET /auth/login params construction.
   */
  buildAuthUrl(pkce: XTwitterPkce): string {
    if (!this.isConfigured()) {
      throw new Error('X Twitter Auth Service not configured. Set expo.extra.xTwitterClientId and expo.extra.xTwitterRedirectUri in app config.');
    }

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      scope: X_SCOPES,
      state: pkce.state,
      code_challenge: pkce.codeChallenge,
      code_challenge_method: 'S256'
    });

    return `${X_AUTH_BASE}?${params.toString()}`;
  }

  /**
   * Exchange authorization code for tokens.
   * Sends { code, codeVerifier } to the Sendifier backend which handles
   * the server-side token exchange (keeps CLIENT_SECRET server-side only).
   */
  async exchangeCode(code: string, codeVerifier: string, apiBaseUrl: string): Promise<XTwitterAuthTokens> {
    const response = await fetch(`${apiBaseUrl}/api/x-twitter/auth/exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, codeVerifier })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error?.error ?? `Token exchange failed: HTTP ${response.status}`);
    }

    const data = await response.json();
    if (!data.success || !data.access_token) {
      throw new Error('Invalid token exchange response');
    }

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
      expires_at: data.expires_at,
      user: data.user
    };
  }

  /**
   * Validates that the OAuth callback URL belongs to us and extracts code + state.
   */
  parseCallbackUrl(url: string): { code: string | null; state: string | null; error: string | null } {
    try {
      const parsed = new URL(url);
      return {
        code: parsed.searchParams.get('code'),
        state: parsed.searchParams.get('state'),
        error: parsed.searchParams.get('error')
      };
    } catch {
      return { code: null, state: null, error: 'Invalid callback URL' };
    }
  }

  private base64url(bytes: Uint8Array): string {
    // Convert Uint8Array → base64 string → url-safe base64
    let binary = '';
    bytes.forEach(b => { binary += String.fromCharCode(b); });
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }
}

export const xTwitterAuthService = new XTwitterAuthService();
